import { type SWRConfiguration } from 'swr';
import type {
  DisasterRecoveryConfig,
  DisasterRecoveryInitiationProposal,
  DisasterRecoverySupportingProposal,
  DRProposalWithReviewData,
} from '@mls-wallet/shared-types';
import {
  useDisasterRecoveryProposalList,
  type ServerDisasterRecoveryProposalsResponse,
} from './use-disaster-recovery-proposal-list';

export type DRPhase = DisasterRecoveryConfig['phase'] | 'CANCELLED';

export type DRConfig = Omit<DisasterRecoveryConfig, 'phase'> & {
  phase: DRPhase;
};

type ActiveDisasterRecoveryInitiationProposal =
  DRProposalWithReviewData<DisasterRecoveryInitiationProposal> | null;

export type ActiveDRSupportingProposal =
  DRProposalWithReviewData<DisasterRecoverySupportingProposal> | null;

type DisasterRecoverySettingsData = {
  drConfig: DRConfig | null;
  drInitiationProposal: ActiveDisasterRecoveryInitiationProposal;
  drCancellationProposal: ActiveDRSupportingProposal;
  drFinalizationProposal: ActiveDRSupportingProposal;
};

type BaseSettingsResult = {
  isLoading: boolean;
  error: Error | undefined;
  refresh: (
    data?:
      | ServerDisasterRecoveryProposalsResponse
      | Promise<ServerDisasterRecoveryProposalsResponse>
      | undefined,
    opts?: { revalidate?: boolean }
  ) => Promise<ServerDisasterRecoveryProposalsResponse | undefined>;
};

type FullSettingsResult = BaseSettingsResult & DisasterRecoverySettingsData;

type UseDisasterRecoverySettingsView = 'config' | 'full';
type UseDisasterRecoverySettingsOptions = {
  view?: UseDisasterRecoverySettingsView;
  // Underlying list hook consumes the full server payload for both views.
  swrOptions?: SWRConfiguration<ServerDisasterRecoveryProposalsResponse>;
};

/**
 * Read DR settings payload. `view: "config"` returns only drConfig; default
 * `view: "full"` returns config + active proposal slices. For simpler typing and
 * lint compatibility, this hook always returns the full shape.
 */
export function useDisasterRecoverySettings(
  options?: UseDisasterRecoverySettingsOptions
): FullSettingsResult {
  const view = options?.view ?? 'full';

  const { data, error, isLoading, refresh } = useDisasterRecoveryProposalList({
    refreshInterval: 15000,
    ...(options?.swrOptions ?? {}),
  });

  const base = { isLoading, error, refresh };

  if (view === 'config') {
    return {
      ...base,
      drConfig: data?.drConfig ?? null,
      drInitiationProposal: null,
      drCancellationProposal: null,
      drFinalizationProposal: null,
    };
  }

  return {
    ...base,
    drConfig: data?.drConfig ?? null,
    drInitiationProposal: data?.drInitiationProposal ?? null,
    drCancellationProposal: data?.drCancellationProposal ?? null,
    drFinalizationProposal: data?.drFinalizationProposal ?? null,
  };
}
